const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Cards/ECheckinCard/ECheckinCard.tsx';
import { useSelector } from 'react-redux';
import * as React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { css, cx } from '@emotion/css';
import Icon from '@mui/material/Icon';
import { theme } from 'App/utils/clientSetupManager';
import { useIntl } from 'react-intl';
import textMap from 'App/pages/DashboardPage/Cards/ECheckinCard/translations';
import { cssFlexBoxStyle } from 'App/components/Card/styles';
import { ECheckinCardInfo } from './ECheckinCardInfo';
import { getIsLoggedInByEmailWithoutPatnr } from '@cistec/common_web/selectors/patientsSelectors';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { Gap } from 'App/components/Gap/Gap';
import { IconDown, IconUp } from 'App/components/Buttons/IconDownOrUp';
import { TransitionOnCondition } from '@cistec/common_web/components/TransitionOnCondition/TransitionOnCondition';
import { FormatMessageFn } from '@cistec/common_web/defs/generalDefs';
import { CaseType, ECheckinWorkItem, StructuredAppointment } from '@cistec/common_web/defs/kisim';
import { isECheckinFinalized, patientNeedsClearing } from 'App/utils/workItemUtils';
import { sortObjectsByPath } from '@cistec/common_web/utils/data';
import { selectECheckinAppointment, useFutureAppointments, usePastAppointments } from 'App/store/appointment';
import { DEFAULT_ECHECKIN_WORKITEM_TKEY, useECheckinItem, useECheckinItems } from 'App/store/eCheckinItems';
import { useFeatureFlags } from 'App/store/featureFlags';
import { useAllowedEcheckinAppointmentSettings } from 'App/store/allowedECheckinAppointmentTypes';

const fullHeight = css`
    height: 100%;
`;

const fullWidth = css`
    width: 100%;
`;

const flexColumn = css`
    display: flex;
    flex-direction: column;
`;

const flexRow = css`
    display: flex;
`;

const alignRight = css`
    margin-left: auto;
`;
const headerIconStyle = css`
    margin-top: ${theme.spacing(1)};
    color: ${theme.palette.primary.main};
`;
const cardActionAreaInline: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
};

const dividerWidth = css`
    width: 100%;
`;

const doneWorkItemButton = css`
    min-width: 100%;
    text-align: center;
`;

export interface AppointmentAndWorkItemData {
    startDat: string;
    termNr: string;
    caseNr?: string;
    kat: string;
    type: string;
    title: string;
    workItemWorkID?: string;
    workItemHasEcheckin?: boolean;
    isECheckinFinalized: boolean;
    patientNeedsClearing: boolean;
    checkinFinalizedDat: string;
}

const getAppointmentAsObject = (
    appointment: StructuredAppointment | undefined,
    formatMessage: FormatMessageFn,
    eCheckinWorkItem?: ECheckinWorkItem
): AppointmentAndWorkItemData => {
    const appointmentAsObject: AppointmentAndWorkItemData = {
        termNr: eCheckinWorkItem?.TKEY && eCheckinWorkItem?.TKEY !== 'PATNR' ? eCheckinWorkItem.TKEY : '',
        startDat: appointment?.STARTDAT || '',
        kat: appointment?.KAT || '',
        type: appointment?.TYP || '',
        title: appointment?.titleFunction ? appointment.titleFunction(formatMessage) : appointment?.TITLE || '',
        caseNr: appointment?.FALLNR || '',
        workItemWorkID: eCheckinWorkItem?.WORKID,
        isECheckinFinalized: eCheckinWorkItem ? isECheckinFinalized(eCheckinWorkItem) : false,
        patientNeedsClearing: eCheckinWorkItem ? patientNeedsClearing(eCheckinWorkItem) : false,
        workItemHasEcheckin: !!eCheckinWorkItem?.CONTENT?.echeckin,
        checkinFinalizedDat: eCheckinWorkItem?.DAT || '',
    };
    return appointmentAsObject;
};

export const getAppointmentsAsObjectAndSomethingMoreWeDoNotUnderstand = (
    allowedCaseTypes: CaseType[],
    formatMessage: FormatMessageFn,
    appointments: StructuredAppointment[] = [],
    eCheckinWorkItems: ECheckinWorkItem[] = []
): AppointmentAndWorkItemData[] => {
    const appointmentsWithFALLNR = appointments
        .filter(appointment => appointment.FALLNR !== undefined)
        .sort(sortObjectsByPath('STARTDAT', false));
    const eCheckinRenderList: AppointmentAndWorkItemData[] = [];

    const { eCheckin: eCheckinConfig } = getConfiguration();
    appointmentsWithFALLNR.forEach(appointment => {
        const termNrInEcheckinWorkItems = getTermNrsFromEcheckinWorkItems(eCheckinWorkItems);
        const kisimTriggeredECheckinWorkItems = getFallNrsKisimTriggeredEcheckinWorkItems(eCheckinWorkItems);

        const fallNr = appointment.FALLNR!; //Forced due to the filter above
        const termNr = appointment.TERMNR;

        // filter item according to case type:
        if (
            !termNrInEcheckinWorkItems.includes(termNr) /*Allow appointments from kisim triggered eCheckinWorkItem*/ &&
            !(allowedCaseTypes || []).includes(appointment.FALLART!)
        ) {
            return;
        }

        const foundCaseAppointment = eCheckinRenderList.find(rec => rec.caseNr === fallNr);
        let echeckinWorkItem: any;

        const findECheckinWorkItem = (fallNr: string, tkey: string = DEFAULT_ECHECKIN_WORKITEM_TKEY) =>
            eCheckinWorkItems.find(workItem => workItem.FALLNR === fallNr && workItem.TKEY === tkey);

        if (
            !foundCaseAppointment &&
            // Skip if there is a kisim triggered workItem existing
            !kisimTriggeredECheckinWorkItems.includes(fallNr)
        ) {
            echeckinWorkItem = findECheckinWorkItem(fallNr);
            eCheckinRenderList.push(getAppointmentAsObject(appointment, formatMessage, echeckinWorkItem));
        } else if (findECheckinWorkItem(fallNr, termNr)) {
            echeckinWorkItem = findECheckinWorkItem(fallNr, termNr);
            eCheckinRenderList.push(getAppointmentAsObject(appointment, formatMessage, echeckinWorkItem));
        }
    });
    return eCheckinRenderList;
};

const getTermNrsFromEcheckinWorkItems = (eCheckinWorkItems: ECheckinWorkItem[]): string[] => {
    if (!eCheckinWorkItems || eCheckinWorkItems.length <= 0) return [];

    return eCheckinWorkItems
        .map(eCheckinWorkItem => eCheckinWorkItem.TKEY)
        .filter(TKEY => TKEY !== 'PATNR') as string[];
};

const getFallNrsKisimTriggeredEcheckinWorkItems = (eCheckinWorkItems: ECheckinWorkItem[] | undefined): string[] => {
    if (!eCheckinWorkItems || eCheckinWorkItems.length <= 0) return [];

    return (
        eCheckinWorkItems
            // only get the ones which are triggered by Kisim (TKEY -> TermNr)
            .filter(eCheckinWorkItem => eCheckinWorkItem.TKEY !== 'PATNR' && !!eCheckinWorkItem.FALLNR)
            .map(eCheckinWorkItem => eCheckinWorkItem.FALLNR) as string[]
    );
};

const getFutureAndTriageAppointments = (): StructuredAppointment[] => {
    const { data: futureAppointments } = useFutureAppointments();
    const { data: pastAppointments } = usePastAppointments();
    const triageAppointments = pastAppointments?.filter(appointment => appointment.TYP === 'TRIAGE') || [];

    return futureAppointments ? [...futureAppointments, ...triageAppointments] : triageAppointments;
};

export const ECheckinCard = () => {
    const { data: featureFlags } = useFeatureFlags();

    const { formatMessage } = useIntl();
    const [showDoneWorkItems, setShowDoneWorkItems] = useState(false);
    const {
        data: allowedEcheckinAppointmentSettings,
        isPendingOrLoading: allowedEcheckinAppointmentSettingsIsLoading,
    } = useAllowedEcheckinAppointmentSettings();

    useEffect(() => {
        if (!allowedEcheckinAppointmentSettingsIsLoading) {
            return;
        }
    }, [allowedEcheckinAppointmentSettingsIsLoading]);

    const upcomingAppointmentAllowedForEcheckin = useSelector(
        selectECheckinAppointment(allowedEcheckinAppointmentSettings.appointmentTypes || [])
    );
    const appointments = getFutureAndTriageAppointments();

    const { data: eCheckinWorkItemWithoutFallNr } = useECheckinItem();
    const singleAppointmentAsObject = getAppointmentAsObject(
        upcomingAppointmentAllowedForEcheckin,
        formatMessage,
        eCheckinWorkItemWithoutFallNr
    );
    const { data: eCheckinWorkItems } = useECheckinItems();
    const workItemAppointmentNrs = getTermNrsFromEcheckinWorkItems(eCheckinWorkItems);

    const filteredAppointments =
        appointments &&
        allowedEcheckinAppointmentSettings.appointmentTypes &&
        appointments.filter(appointment => {
            return (
                (!featureFlags.dashboard.disableAutoEcheckin &&
                    allowedEcheckinAppointmentSettings.appointmentTypes.includes(appointment.TYP)) ||
                workItemAppointmentNrs.includes(appointment.TERMNR) // do not filter out appointments which occurs in eCheckinWorkItems
            );
        });

    const appointmentsAsObject = getAppointmentsAsObjectAndSomethingMoreWeDoNotUnderstand(
        allowedEcheckinAppointmentSettings.caseTypes,
        formatMessage,
        filteredAppointments,
        eCheckinWorkItems
    );

    const openEcheckinCount = appointmentsAsObject.filter(appointmentAndWorkItemData => {
        return !appointmentAndWorkItemData.isECheckinFinalized;
    }).length;
    const recentCaseAppCount = appointmentsAsObject.filter(appointmentAndWorkItemData => {
        return appointmentAndWorkItemData.isECheckinFinalized && !appointmentAndWorkItemData.patientNeedsClearing;
    }).length;

    const userIsLoggedInByEmailOnly = useSelector(getIsLoggedInByEmailWithoutPatnr);

    const toggleShowDoneWorkItems = () => {
        setShowDoneWorkItems(!showDoneWorkItems);
    };

    const divider = (
        <Fragment key={`divider`}>
            <Gap />
            <Divider className={dividerWidth} />
        </Fragment>
    );

    const children = userIsLoggedInByEmailOnly ? (
        <ECheckinCardInfo recentCaseAppointment={singleAppointmentAsObject} />
    ) : (
        appointmentsAsObject?.map((appointmentAndWorkItemData, index) => {
            const child = (
                <Fragment key={`ECheckinCardInfo_${index}`}>
                    {index > 0 ? divider : null}
                    <ECheckinCardInfo recentCaseAppointment={appointmentAndWorkItemData} />
                </Fragment>
            );
            return appointmentAndWorkItemData.isECheckinFinalized &&
                !appointmentAndWorkItemData.patientNeedsClearing ? (
                <TransitionOnCondition key={`TransitionOnCondition_${index}`} val={showDoneWorkItems}>
                    {child}
                </TransitionOnCondition>
            ) : (
                child
            );
        })
    );

    const textDoneUndone = showDoneWorkItems
        ? formatMessage(textMap.hideDoneWorkItems)
        : formatMessage(textMap.doneWorkItems, {
              count: recentCaseAppCount,
          });
    return (
        <Grid size={{ xs: 12 }}>
            <Card className={fullHeight}>
                <Grid style={cardActionAreaInline}>
                    <CardContent className={fullWidth}>
                        <Box
                            sx={{
                                display: 'flex',
                            }}
                        >
                            <Box
                                className={cssFlexBoxStyle}
                                sx={{
                                    flexGrow: 1,
                                    gap: 1,
                                }}
                            >
                                <div>
                                    <Icon className={cx('icons8-check-on-badge', headerIconStyle)} component="span" />
                                </div>
                                <div className={fullWidth}>
                                    <div className={flexRow}>
                                        <Typography component="h2" sx={{ color: 'primary.main' }} variant="subtitle1">
                                            {formatMessage(textMap.cardTitle)}
                                        </Typography>
                                        {!userIsLoggedInByEmailOnly && (
                                            <div className={alignRight}>
                                                <Button
                                                    color="secondary"
                                                    component="h2"
                                                    variant="text"
                                                    size={'small'}
                                                    onClick={() => toggleShowDoneWorkItems()}
                                                    className={doneWorkItemButton}
                                                    endIcon={
                                                        showDoneWorkItems ? (
                                                            <IconUp disabled={!recentCaseAppCount} />
                                                        ) : (
                                                            <IconDown disabled={!recentCaseAppCount} />
                                                        )
                                                    }
                                                    disabled={!recentCaseAppCount}
                                                >
                                                    {textDoneUndone}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    {openEcheckinCount === 0 && (
                                        <div className={flexRow}>
                                            <Typography>{formatMessage(textMap.noOpenEcheckin)}</Typography>
                                        </div>
                                    )}
                                    <Grid className={flexColumn}>{children}</Grid>
                                </div>
                            </Box>
                        </Box>
                    </CardContent>
                </Grid>
            </Card>
        </Grid>
    );
};
